import { deleteWoundOrderOptions, type DeleteWoundOrderCommand, type WoundOrderViewModel } from '@generated';
import { SectionListRow } from '~/shared/section/section-list/SectionListRow';
import { useQuery } from '@tanstack/react-query';
import { useMutation } from '~/util/http/useMutation';
import { Prompt } from '~/shared/components/Prompt';
import { useGetWoundOrders } from '../queries';
import { useFullOrder } from '~/features/orders/orders/FullOrderContext';
import { WoundOrderFormModal } from './WoundOrderFormModal';
import { List } from '~/shared/components/List';

export function WoundOrderRow({ woundOrder }: { woundOrder: WoundOrderViewModel }) {
	const { fullOrder } = useFullOrder();
	const { refetch } = useQuery(useGetWoundOrders(fullOrder.id));
	const deleteWoundOrder = useMutation<void, DeleteWoundOrderCommand>(deleteWoundOrderOptions);

	const onDeleteWound = () => {
		deleteWoundOrder.mutate(
			{ woundOrderId: woundOrder.id },
			{
				onSuccess: () => {
					refetch();
				}
			}
		);
	};

	return (
		<SectionListRow
			label={woundOrder.wound.woundLabel}
			removeButtonProps={{
				label: 'Remove Wound Order',
				isPending: deleteWoundOrder.isPending,
				renderModal: closeModal => (
					<Prompt
						title="Remove Wound Order"
						text="Are you sure you want to remove this wound order?"
						onAction={() => {
							onDeleteWound();
							closeModal();
						}}
						onCancel={closeModal}
					/>
				)
			}}
			editButtonProps={{
				label: 'Edit Wound Order',
				renderModal: closeModal => <WoundOrderFormModal fullOrder={fullOrder} woundOrder={woundOrder} closeModal={closeModal} />
			}}
		>
			<div className="flex flex-col gap-4 px-3 py-2.5">
				<span>Frequency: {woundOrder.frequency}</span>
				{woundOrder.prestructuredCareInstructions && woundOrder.orderComponents.length === 0 ? (
					<span className="text-sm text-gray-700">{woundOrder.prestructuredCareInstructions}</span>
				) : (
					<List>
						{woundOrder.orderComponents.map(x => (
							<List.ListItem key={x.id}>
								<div className="flex justify-between gap-2">
									<span className="text-sm text-gray-700">
										{x.woundOrderComponentTypeLabel}: {x.product}
									</span>
								</div>
							</List.ListItem>
						))}
					</List>
				)}
			</div>
		</SectionListRow>
	);
}
